import { ethers } from 'ethers';
import { CRYPTO_CLICKER_ABI } from '../contracts';
import toast from 'react-hot-toast';

declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      request: (args: { method: string; params?: any[] }) => Promise<any>;
      on: (eventName: string, handler: (...args: any[]) => void) => void;
      removeAllListeners: () => void;
      selectedAddress?: string;
      chainId?: string;
      networkVersion?: string;
      isConnected?: () => boolean;
    };
  }
}

class ContractService {
  private _provider: ethers.BrowserProvider | null = null;
  private _signer: ethers.JsonRpcSigner | null = null;
  private _contract: ethers.Contract | null = null;
  private contractAddress: string;
  private initialized = false;

  constructor() {
    this.contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS;
    if (!this.contractAddress) {
      console.error('Contract address not found in environment variables');
    }
  }

  // Getter for contract
  get contract(): ethers.Contract | null {
    return this._contract;
  }

  async initialize() {
    if (this.initialized) return true;

    try {
      if (!window.ethereum) {
        toast.error('MetaMask not detected. Please install MetaMask to continue.');
        return false;
      }

      try {
        await window.ethereum.request({ 
          method: 'eth_requestAccounts',
          params: [{
            eth_accounts: {},
            snap_dialog: true
          }]
        });
      } catch (permissionError: any) {
        if (permissionError.code === 4001) {
          toast.error('Please connect your wallet to continue');
        } else {
          toast.error('Error connecting to MetaMask');
        }
        return false;
      }

      this._provider = new ethers.BrowserProvider(window.ethereum, {
        name: 'Ethereum Mainnet',
        chainId: 1
      });

      this._signer = await this._provider.getSigner();
      
      const accounts = await this._provider.listAccounts();
      if (!accounts || accounts.length === 0) {
        toast.error('Please unlock your MetaMask wallet');
        return false;
      }

      if (!this.contractAddress) {
        toast.error('Contract address not configured');
        return false;
      }

      this._contract = new ethers.Contract(
        this.contractAddress,
        CRYPTO_CLICKER_ABI,
        this._signer
      );

      try {
        await this._contract.getAddress();
      } catch (error) {
        console.error('Contract verification failed:', error);
        toast.error('Error connecting to game contract');
        return false;
      }

      this.initialized = true;
      return true;

    } catch (error: any) {
      console.error('Contract initialization error:', error);
      
      if (error.code === -32002) {
        toast.error('MetaMask request already pending. Please check your MetaMask wallet.');
      } else if (error.code === 4001) {
        toast.error('Please approve the connection request in MetaMask');
      } else if (error.message?.includes('already processing eth_requestAccounts')) {
        toast.error('Connection request pending. Please check MetaMask');
      } else if (error.message?.includes('Lockdown')) {
        toast.error('Please unlock your MetaMask wallet to continue');
      } else {
        toast.error('Error initializing contract. Please refresh and try again.');
      }
      
      return false;
    }
  }

  async estimateGas(to: string, value: bigint) {
    if (!this._provider || !this._signer) {
      await this.initialize();
      if (!this._provider || !this._signer) {
        throw new Error('Provider or signer not initialized');
      }
    }

    try {
      const feeData = await this._provider.getFeeData();
      if (!feeData.maxFeePerGas) {
        throw new Error('Could not estimate gas price');
      }

      const gasLimit = BigInt(21000); // Standard ETH transfer
      const gasCost = feeData.maxFeePerGas * gasLimit;

      return {
        gasLimit,
        maxFeePerGas: feeData.maxFeePerGas,
        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas || feeData.maxFeePerGas,
        gasCost
      };
    } catch (error) {
      console.error('Gas estimation error:', error);
      throw error;
    }
  }

  async sendTransaction(to: string, value: bigint) {
    if (!this._signer) {
      await this.initialize();
      if (!this._signer) {
        throw new Error('Signer not initialized');
      }
    }

    try {
      const { gasLimit, maxFeePerGas, maxPriorityFeePerGas } = await this.estimateGas(to, value);

      const tx = await this._signer.sendTransaction({
        to,
        value,
        gasLimit,
        maxFeePerGas,
        maxPriorityFeePerGas
      });

      return tx;
    } catch (error: any) {
      if (error.code === 4001) {
        throw new Error('Transaction rejected by user');
      } else if (error.code === -32603) {
        throw new Error('Internal MetaMask error');
      } else if (error.message?.includes('insufficient funds')) {
        throw new Error('Insufficient ETH to cover gas fees');
      } else if (error.message?.includes('Lockdown')) {
        throw new Error('Please unlock your MetaMask wallet');
      }
      throw error;
    }
  }

  async registerClicks(clicks: number) {
    if (!this._contract) {
      await this.initialize();
      if (!this._contract) {
        throw new Error('Contract not initialized');
      }
    }

    try {
      const tx = await this._contract.registerClicks(clicks);
      await tx.wait();
      return tx;
    } catch (error) {
      console.error('Error registering clicks:', error);
      throw error;
    }
  }

  async withdraw(coinsAmount: number) {
    if (!this._contract) {
      await this.initialize();
      if (!this._contract) {
        throw new Error('Contract not initialized');
      }
    }

    try {
      const tx = await this._contract.withdraw(coinsAmount);
      await tx.wait();
      return tx;
    } catch (error) {
      console.error('Error withdrawing:', error);
      throw error;
    }
  }

  async getMiningStats(address: string) {
    if (!this._contract) {
      await this.initialize();
      if (!this._contract) {
        throw new Error('Contract not initialized');
      }
    }

    try {
      const stats = await this._contract.getMiningStats(address);
      return {
        currentClicks: stats[0].toString(),
        pendingWei: stats[1].toString(),
        clicksNeededForNextCycle: stats[2].toString()
      };
    } catch (error) {
      console.error('Error getting mining stats:', error);
      throw error;
    }
  }

  setupEventListeners() {
    if (!window.ethereum) return;

    // Handle chain changes
    window.ethereum.on('chainChanged', () => {
      toast.error('Network changed. Please refresh the page.');
      window.location.reload();
    });

    // Handle account changes
    window.ethereum.on('accountsChanged', (accounts: string[]) => {
      if (accounts.length === 0) {
        toast.error('Please connect your wallet');
      } else {
        toast('Account changed. Please refresh the page.');
      }
      window.location.reload();
    });

    // Handle disconnect
    window.ethereum.on('disconnect', () => {
      toast.error('Wallet disconnected');
      this.initialized = false;
      window.location.reload();
    });

    // Handle unlock
    window.ethereum.on('unlock', () => {
      this.initialized = false;
      this.initialize().catch(console.error);
    });
  }

  removeEventListeners() {
    if (!window.ethereum) return;
    window.ethereum.removeAllListeners();
  }
}

export const contractService = new ContractService();